import React, { useState, useEffect, useContext, useMemo } from 'react';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Edit2,
  Trash2,
  Package,
  Euro,
  TrendingUp,
  Boxes,
  Archive,
  AlertCircle,
  Search,
  Filter,
  MapPin
} from 'lucide-react';

const InventoryPage = () => {
  const { API, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';

  // Redirect Helfer zum Dashboard
  useEffect(() => {
    if (!isAdmin) {
      toast.error('Keine Berechtigung für Inventar');
      navigate('/', { replace: true });
    }
  }, [isAdmin, navigate]);

  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteConfirmItem, setDeleteConfirmItem] = useState(null);
  
  // Search & Filter
  const [searchQuery, setSearchQuery] = useState('');
  const [locationFilter, setLocationFilter] = useState('all');
  
  // Track which price field was last edited for auto-calculation
  const [lastPriceEdited, setLastPriceEdited] = useState(null);

  const emptyForm = {
    code: '',
    name: '',
    article_number: '',
    supplier: '',
    ve: '',
    price_net: '',
    price_gross: '',
    mhd_date: '',
    quantity: '',
    storage_location_type: 'Lager',
    storage_location_text: '',
    item_type: 'Verbrauchsartikel',
    min_stock: '0'
  };

  const [formData, setFormData] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (isAdmin) {
      fetchData();
    }
  }, [isAdmin]);

  const fetchData = async () => {
    try {
      const [itemsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/inventory`),
        axios.get(`${API}/inventory/summary`)
      ]);
      setItems(itemsRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Keine Berechtigung');
        navigate('/', { replace: true });
      } else {
        toast.error('Fehler beim Laden des Inventars');
      }
    } finally {
      setLoading(false);
    }
  };

  // Sortierte und gefilterte Items
  const filteredItems = useMemo(() => {
    let result = [...items];
    
    // Alphabetische Sortierung nach Artikelname (A → Z)
    result.sort((a, b) => {
      const nameA = (a.name || '').toLowerCase();
      const nameB = (b.name || '').toLowerCase();
      return nameA.localeCompare(nameB, 'de');
    });
    
    // Lagerort-Filter
    if (locationFilter !== 'all') {
      result = result.filter(item => item.storage_location_type === locationFilter);
    }
    
    // Suche
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter(item => 
        (item.name || '').toLowerCase().includes(query) ||
        (item.code || '').toLowerCase().includes(query) ||
        (item.article_number || '').toLowerCase().includes(query) ||
        (item.supplier || '').toLowerCase().includes(query)
      );
    }
    
    return result;
  }, [items, locationFilter, searchQuery]);

  // Price calculation helpers
  const calculateGrossFromNet = (net) => {
    if (net === '' || net === null || isNaN(parseFloat(net))) return '';
    return (Math.round(parseFloat(net) * 1.19 * 100) / 100).toFixed(2);
  };

  const calculateNetFromGross = (gross) => {
    if (gross === '' || gross === null || isNaN(parseFloat(gross))) return '';
    return (Math.round((parseFloat(gross) / 1.19) * 100) / 100).toFixed(2);
  };

  const arePricesConsistent = (net, gross) => {
    if (net === '' || gross === '' || net === null || gross === null) return true;
    const netVal = parseFloat(net);
    const grossVal = parseFloat(gross);
    if (isNaN(netVal) || isNaN(grossVal)) return true;
    
    const expectedGross = Math.round(netVal * 1.19 * 100) / 100;
    const diff = Math.abs(grossVal - expectedGross);
    return diff <= 0.01;
  };

  const handlePriceNetChange = (value) => {
    setLastPriceEdited('net');
    const newGross = calculateGrossFromNet(value);
    setFormData(prev => ({
      ...prev,
      price_net: value,
      price_gross: newGross
    }));
    if (formErrors.price) {
      setFormErrors(prev => ({ ...prev, price: null }));
    }
  };

  const handlePriceGrossChange = (value) => {
    setLastPriceEdited('gross');
    const newNet = calculateNetFromGross(value);
    setFormData(prev => ({
      ...prev,
      price_gross: value,
      price_net: newNet
    }));
    if (formErrors.price) {
      setFormErrors(prev => ({ ...prev, price: null }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.code.trim()) errors.code = 'Code ist erforderlich';
    if (!formData.name.trim()) errors.name = 'Artikel ist erforderlich';
    if (formData.ve === '' || formData.ve < 0) errors.ve = 'VE ist erforderlich (>=0)';
    if (formData.quantity === '' || formData.quantity < 0) errors.quantity = 'Stückzahl ist erforderlich (>=0)';
    if (formData.min_stock === '' || parseInt(formData.min_stock) < 0) errors.min_stock = 'Mindestbestand muss >= 0 sein';
    
    const hasNet = formData.price_net !== '' && formData.price_net !== null;
    const hasGross = formData.price_gross !== '' && formData.price_gross !== null;
    
    if (!hasNet && !hasGross) {
      errors.price = 'Mindestens Netto oder Brutto muss angegeben werden';
    } else if (hasNet && hasGross && !arePricesConsistent(formData.price_net, formData.price_gross)) {
      errors.price = 'Netto und Brutto sind inkonsistent (19% MwSt). Bitte korrigieren Sie einen der Werte.';
    }
    
    if (formData.storage_location_type === 'Sonstiges' && !formData.storage_location_text.trim()) {
      errors.storage_location_text = 'Lagerort-Beschreibung ist Pflicht bei Sonstiges';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Bitte alle Pflichtfelder ausfüllen und Fehler korrigieren');
      return;
    }

    try {
      let priceNet = formData.price_net !== '' ? parseFloat(formData.price_net) : null;
      let priceGross = formData.price_gross !== '' ? parseFloat(formData.price_gross) : null;
      
      if (priceNet !== null && priceGross === null) {
        priceGross = Math.round(priceNet * 1.19 * 100) / 100;
      } else if (priceGross !== null && priceNet === null) {
        priceNet = Math.round((priceGross / 1.19) * 100) / 100;
      }

      const payload = {
        code: formData.code.trim(),
        name: formData.name.trim(),
        article_number: formData.article_number.trim() || null,
        supplier: formData.supplier.trim() || null,
        ve: parseInt(formData.ve) || 0,
        price_net: priceNet,
        price_gross: priceGross,
        mhd_date: formData.mhd_date || null,
        quantity: parseInt(formData.quantity) || 0,
        storage_location_type: formData.storage_location_type,
        storage_location_text: formData.storage_location_type === 'Sonstiges' ? formData.storage_location_text.trim() : null,
        item_type: formData.item_type,
        min_stock: parseInt(formData.min_stock) || 0
      };

      if (editingItem) {
        await axios.put(`${API}/inventory/${editingItem.id}`, payload);
        toast.success('Inventar-Artikel aktualisiert');
      } else {
        await axios.post(`${API}/inventory`, payload);
        toast.success('Inventar-Artikel erstellt');
      }

      setShowDialog(false);
      setEditingItem(null);
      setFormData(emptyForm);
      setLastPriceEdited(null);
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 409) {
        toast.error('Code bereits vergeben');
        setFormErrors({ ...formErrors, code: 'Code bereits vergeben' });
      } else if (error.response?.status === 422 && detail?.includes('inkonsistent')) {
        toast.error(detail);
        setFormErrors({ ...formErrors, price: detail });
      } else if (detail) {
        toast.error(detail);
      } else {
        toast.error('Fehler beim Speichern');
      }
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      code: item.code || '',
      name: item.name || '',
      article_number: item.article_number || '',
      supplier: item.supplier || '',
      ve: item.ve?.toString() || '',
      price_net: item.price_net?.toString() || '',
      price_gross: item.price_gross?.toString() || '',
      mhd_date: item.mhd_date || '',
      quantity: item.quantity?.toString() || '',
      storage_location_type: item.storage_location_type || 'Lager',
      storage_location_text: item.storage_location_text || '',
      item_type: item.item_type || 'Verbrauchsartikel',
      min_stock: (item.min_stock ?? 0).toString()
    });
    setFormErrors({});
    setLastPriceEdited(null);
    setShowDialog(true);
  };

  const handleDelete = async () => {
    if (!deleteConfirmItem) return;
    
    try {
      await axios.delete(`${API}/inventory/${deleteConfirmItem.id}`);
      toast.success('Inventar-Artikel gelöscht');
      setDeleteConfirmItem(null);
      fetchData();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const openCreateDialog = () => {
    setEditingItem(null);
    setFormData(emptyForm);
    setFormErrors({});
    setLastPriceEdited(null);
    setShowDialog(true);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value || 0);
  };

  const formatCurrencyCompact = (value) => {
    const num = value || 0;
    if (num >= 1000) {
      return new Intl.NumberFormat('de-DE', { 
        style: 'currency', 
        currency: 'EUR',
        notation: 'compact',
        maximumFractionDigits: 1
      }).format(num);
    }
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(num);
  };

  // Lagerort Badge Farbe
  const getLocationBadgeClass = (locationType) => {
    switch (locationType) {
      case 'Lager':
        return 'bg-blue-100 text-blue-700';
      case 'Büro':
        return 'bg-green-100 text-green-700';
      case 'Werkstatt':
        return 'bg-amber-100 text-amber-700';
      case 'Sonstiges':
        return 'bg-slate-100 text-slate-700';
      default:
        return 'bg-slate-100 text-slate-700';
    }
  };

  // Bestandsfarbe für Kacheln
  const getStockColor = (quantity) => {
    if (quantity <= 3) return 'text-red-600 bg-red-50';
    if (quantity <= 8) return 'text-orange-600 bg-orange-50';
    return 'text-green-600 bg-green-50';
  };

  const getStockBorderColor = (quantity) => {
    if (quantity <= 3) return 'border-red-200';
    if (quantity <= 8) return 'border-orange-200';
    return 'border-green-200';
  };

  if (!isAdmin) {
    return null;
  }

  if (loading) {
    return <div className="flex justify-center items-center h-64">Laden...</div>;
  }

  return (
    <div className="w-full max-w-full overflow-x-hidden space-y-4 sm:space-y-6 animate-slide-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl sm:text-4xl font-bold mb-1 sm:mb-2" style={{ fontFamily: 'Space Grotesk' }}>
            Inventar
          </h1>
          <p className="text-sm sm:text-base text-slate-600">Verwaltung aller Inventar-Artikel</p>
        </div>
        <Button onClick={openCreateDialog} className="bg-gradient-to-r from-blue-500 to-indigo-500 flex-shrink-0">
          <Plus size={20} className="mr-2" />
          Neu
        </Button>
      </div>

      {/* Dashboard Kacheln - UNVERÄNDERT */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3 lg:gap-4">
          <Card className="p-3 sm:p-4 bg-gradient-to-br from-green-50 to-emerald-50 border-green-200 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm text-green-600 font-medium truncate">Warenwert</p>
                <p className="text-lg sm:text-xl lg:text-2xl font-bold text-green-700 truncate">
                  <span className="hidden sm:inline">{formatCurrency(summary.total_value_gross)}</span>
                  <span className="sm:hidden">{formatCurrencyCompact(summary.total_value_gross)}</span>
                </p>
              </div>
              <Euro className="text-green-500 flex-shrink-0 hidden xs:block" size={24} />
            </div>
          </Card>
          
          <Card className="p-3 sm:p-4 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm text-blue-600 font-medium truncate">Investitionsgüter</p>
                <p className="text-lg sm:text-xl lg:text-2xl font-bold text-blue-700 truncate">
                  <span className="hidden sm:inline">{formatCurrency(summary.invest_value_gross)}</span>
                  <span className="sm:hidden">{formatCurrencyCompact(summary.invest_value_gross)}</span>
                </p>
              </div>
              <TrendingUp className="text-blue-500 flex-shrink-0 hidden xs:block" size={24} />
            </div>
          </Card>
          
          <Card className="p-3 sm:p-4 bg-gradient-to-br from-purple-50 to-violet-50 border-purple-200 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm text-purple-600 font-medium truncate">Verbrauchsgüter</p>
                <p className="text-lg sm:text-xl lg:text-2xl font-bold text-purple-700 truncate">
                  <span className="hidden sm:inline">{formatCurrency(summary.consumables_value_gross)}</span>
                  <span className="sm:hidden">{formatCurrencyCompact(summary.consumables_value_gross)}</span>
                </p>
              </div>
              <Package className="text-purple-500 flex-shrink-0 hidden xs:block" size={24} />
            </div>
          </Card>
          
          <Card className="p-3 sm:p-4 bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm text-amber-600 font-medium truncate">Sonstige</p>
                <p className="text-lg sm:text-xl lg:text-2xl font-bold text-amber-700 truncate">
                  <span className="hidden sm:inline">{formatCurrency(summary.other_value_gross)}</span>
                  <span className="sm:hidden">{formatCurrencyCompact(summary.other_value_gross)}</span>
                </p>
              </div>
              <Archive className="text-amber-500 flex-shrink-0 hidden xs:block" size={24} />
            </div>
          </Card>
          
          <Card className="p-3 sm:p-4 bg-gradient-to-br from-slate-50 to-gray-50 border-slate-200 min-w-0 col-span-2 sm:col-span-1">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm text-slate-600 font-medium truncate">Artikel</p>
                <p className="text-lg sm:text-xl lg:text-2xl font-bold text-slate-700">{summary.total_quantity}</p>
              </div>
              <Boxes className="text-slate-500 flex-shrink-0 hidden xs:block" size={24} />
            </div>
          </Card>
        </div>
      )}

      {/* Suche & Filter */}
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 sm:items-center">
          {/* Suchfeld */}
          <div className="relative flex-1">
            <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
            <Input
              type="text"
              placeholder="Suche nach Artikel, Code, Artikelnummer, Bezugsquelle..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-inventory-input"
            />
          </div>

          {/* Lagerort-Filter */}
          <div className="flex items-center gap-2">
            <Filter size={18} className="text-slate-500 flex-shrink-0" />
            <Select value={locationFilter} onValueChange={setLocationFilter}>
              <SelectTrigger className="w-40" data-testid="location-filter">
                <SelectValue placeholder="Lagerort" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Lagerorte</SelectItem>
                <SelectItem value="Lager">Lager</SelectItem>
                <SelectItem value="Büro">Büro</SelectItem>
                <SelectItem value="Werkstatt">Werkstatt</SelectItem>
                <SelectItem value="Sonstiges">Sonstiges</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Anzahl */}
          <div className="text-sm text-slate-500 sm:border-l sm:pl-4">
            {filteredItems.length} {filteredItems.length === 1 ? 'Artikel' : 'Artikel'}
          </div>
        </div>
      </Card>

      {/* Kachel-Grid */}
      {filteredItems.length === 0 ? (
        <Card className="p-12">
          <div className="text-center text-slate-500">
            <Package size={48} className="mx-auto mb-4 text-slate-300" />
            <p className="text-lg">Keine Inventar-Artikel gefunden</p>
            {(searchQuery || locationFilter !== 'all') && (
              <p className="text-sm mt-2">
                {searchQuery && `Suche nach "${searchQuery}" `}
                {searchQuery && locationFilter !== 'all' && 'mit '}
                {locationFilter !== 'all' && `Filter "${locationFilter}" `}
                ergab keine Treffer.{' '}
                <button 
                  onClick={() => { setSearchQuery(''); setLocationFilter('all'); }} 
                  className="text-blue-500 hover:underline"
                >
                  Filter zurücksetzen
                </button>
              </p>
            )}
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredItems.map((item) => (
            <Card 
              key={item.id} 
              className={`p-4 hover:shadow-lg transition-shadow cursor-pointer flex flex-col min-h-[180px] border-2 ${getStockBorderColor(item.quantity)}`}
              onClick={() => handleEdit(item)}
              data-testid={`inventory-card-${item.code}`}
            >
              {/* Header mit Code und Bestand */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-50 rounded-lg">
                    <Package className="text-blue-600" size={20} />
                  </div>
                  <div>
                    <p className="font-mono text-sm text-slate-500">{item.code}</p>
                  </div>
                </div>
                
                {/* Bestand-Badge rechts */}
                <div className={`px-3 py-2 rounded-lg font-bold text-2xl ${getStockColor(item.quantity)}`}>
                  {item.quantity}
                </div>
              </div>

              {/* Artikelname */}
              <h3 className="font-semibold text-lg text-slate-900 mb-2 line-clamp-2">
                {item.name}
              </h3>

              {/* Artikelnummer (falls vorhanden) */}
              {item.article_number && (
                <p className="text-sm text-slate-500 mb-1">
                  Art.-Nr.: {item.article_number}
                </p>
              )}

              {/* Bezugsquelle (falls vorhanden) */}
              {item.supplier && (
                <p className="text-sm text-slate-400 mb-2">
                  {item.supplier}
                </p>
              )}

              {/* Spacer um Lagerort nach unten zu pushen */}
              <div className="flex-1" />

              {/* Lagerort Badge */}
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100">
                <MapPin size={14} className="text-slate-400" />
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getLocationBadgeClass(item.storage_location_type)}`}>
                  Lagerort – {item.storage_location_type}
                </span>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingItem ? 'Inventar-Artikel bearbeiten' : 'Neuer Inventar-Artikel'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Code *</Label>
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  placeholder="z.B. INV-001"
                  className={formErrors.code ? 'border-red-500' : ''}
                />
                {formErrors.code && <p className="text-red-500 text-xs mt-1">{formErrors.code}</p>}
              </div>
              <div>
                <Label>Artikel *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Artikelname"
                  className={formErrors.name ? 'border-red-500' : ''}
                />
                {formErrors.name && <p className="text-red-500 text-xs mt-1">{formErrors.name}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Artikelnummer</Label>
                <Input
                  value={formData.article_number}
                  onChange={(e) => setFormData({ ...formData, article_number: e.target.value })}
                  placeholder="Optional"
                />
              </div>
              <div>
                <Label>Bezugsquelle</Label>
                <Input
                  value={formData.supplier}
                  onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
                  placeholder="Optional"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
              <div>
                <Label className="text-xs sm:text-sm">VE *</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.ve}
                  onChange={(e) => setFormData({ ...formData, ve: e.target.value })}
                  placeholder="0"
                  className={formErrors.ve ? 'border-red-500' : ''}
                />
                {formErrors.ve && <p className="text-red-500 text-xs mt-1">{formErrors.ve}</p>}
              </div>
              <div>
                <Label className="text-xs sm:text-sm">Stück *</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                  placeholder="0"
                  className={formErrors.quantity ? 'border-red-500' : ''}
                />
                {formErrors.quantity && <p className="text-red-500 text-xs mt-1">{formErrors.quantity}</p>}
              </div>
              <div>
                <Label className="text-xs sm:text-sm">Mindest. *</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.min_stock}
                  onChange={(e) => setFormData({ ...formData, min_stock: e.target.value })}
                  placeholder="0"
                  className={formErrors.min_stock ? 'border-red-500' : ''}
                />
                {formErrors.min_stock && <p className="text-red-500 text-xs mt-1">{formErrors.min_stock}</p>}
              </div>
              <div>
                <Label className="text-xs sm:text-sm">MHD</Label>
                <Input
                  type="date"
                  value={formData.mhd_date}
                  onChange={(e) => setFormData({ ...formData, mhd_date: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>EK Netto (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.price_net}
                  onChange={(e) => handlePriceNetChange(e.target.value)}
                  placeholder="0.00"
                  className={formErrors.price ? 'border-amber-500' : ''}
                />
                {lastPriceEdited === 'net' && (
                  <p className="text-slate-400 text-xs mt-1">Brutto wird berechnet</p>
                )}
              </div>
              <div>
                <Label>EK Brutto (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.price_gross}
                  onChange={(e) => handlePriceGrossChange(e.target.value)}
                  placeholder="0.00"
                  className={formErrors.price ? 'border-amber-500' : ''}
                />
                {lastPriceEdited === 'gross' && (
                  <p className="text-slate-400 text-xs mt-1">Netto wird berechnet</p>
                )}
              </div>
            </div>
            {formErrors.price && (
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
                <AlertCircle className="text-amber-600 flex-shrink-0 mt-0.5" size={16} />
                <p className="text-amber-700 text-sm">{formErrors.price}</p>
              </div>
            )}
            <p className="text-slate-500 text-xs">
              Mind. Netto oder Brutto angeben. Fehlender Wert wird automatisch berechnet (19% MwSt).
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Lagerort *</Label>
                <Select
                  value={formData.storage_location_type}
                  onValueChange={(value) => setFormData({ ...formData, storage_location_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Lager">Lager</SelectItem>
                    <SelectItem value="Büro">Büro</SelectItem>
                    <SelectItem value="Werkstatt">Werkstatt</SelectItem>
                    <SelectItem value="Sonstiges">Sonstiges</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Art *</Label>
                <Select
                  value={formData.item_type}
                  onValueChange={(value) => setFormData({ ...formData, item_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Invest">Invest</SelectItem>
                    <SelectItem value="Verbrauchsartikel">Verbrauchsartikel</SelectItem>
                    <SelectItem value="Sonstiges">Sonstiges</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {formData.storage_location_type === 'Sonstiges' && (
              <div>
                <Label>Lagerort-Beschreibung *</Label>
                <Input
                  value={formData.storage_location_text}
                  onChange={(e) => setFormData({ ...formData, storage_location_text: e.target.value })}
                  placeholder="Beschreibung des Lagerorts"
                  className={formErrors.storage_location_text ? 'border-red-500' : ''}
                />
                {formErrors.storage_location_text && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.storage_location_text}</p>
                )}
              </div>
            )}

            {/* Löschen-Button im Edit-Modus */}
            {editingItem && (
              <div className="pt-2 border-t">
                <Button
                  type="button"
                  variant="outline"
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  onClick={(e) => {
                    e.preventDefault();
                    setShowDialog(false);
                    setDeleteConfirmItem(editingItem);
                  }}
                >
                  <Trash2 size={16} className="mr-2" />
                  Artikel löschen
                </Button>
              </div>
            )}

            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)} className="w-full sm:w-auto">
                Abbrechen
              </Button>
              <Button type="submit" className="w-full sm:w-auto">
                {editingItem ? 'Speichern' : 'Erstellen'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog - Doppelte Sicherheitsabfrage */}
      <Dialog open={!!deleteConfirmItem} onOpenChange={() => setDeleteConfirmItem(null)}>
        <DialogContent className="w-[95vw] max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle size={20} />
              Inventarartikel wirklich löschen?
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600">
              Möchten Sie den Artikel <strong className="break-all">"{deleteConfirmItem?.name}"</strong> (Code: {deleteConfirmItem?.code}) wirklich löschen?
            </p>
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-700 text-sm font-medium">
                ⚠️ Diese Aktion kann nicht rückgängig gemacht werden.
              </p>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setDeleteConfirmItem(null)} className="w-full sm:w-auto">
              Abbrechen
            </Button>
            <Button variant="destructive" onClick={handleDelete} className="w-full sm:w-auto">
              <Trash2 size={16} className="mr-2" />
              Endgültig löschen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InventoryPage;
